import { Button, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddButton from "../../../components/Buttons/AddButton";
import DeleteButton from "../../../components/Buttons/DeleteButton";
import { Desc, Subtitle, Title } from "../../../components/Desc";
import ProgressIndicator from "../../../components/ProgressIndicator";
import strings from "../../../config/localization";
import {
  useAllocatedPersonnelDetailsMutation,
  useReplacePersonnelAllocationsMutation,
  useModifyPersonnelAssessmentMutation,
} from "../../../services/appln";
import SelectPersonnel from "./SelectPersonnel";
import { getBaseURL } from "../../constants";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

interface Personnel {
  designationId: string;
  designationName: string;
  district: string;
  id: string;
  idNumber: string;
  mobileNumber: string;
  name: string;
  organization: string;
  serialNo: number;
}

function ShowReductionDialog(props: any) {
  return (
    <Dialog open={props.state}>
      <DialogTitle id="alert-dialog-title">
        {
          "Allocated count has reduced. Do you want to reduce the assessment count?"
        }
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {
            "Allocated count has reduced. Do you want to reduce the assessment count?"
          }
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.disagreeHandler();
          }}
        >
          {"No"}
        </Button>
        <Button
          onClick={() => {
            props.agreeHandler();
          }}
        >
          {"Yes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PersonnelAllocation() {
  const navigate = useNavigate();

  const params = useParams();
  console.log("PersonnelAllocation, params:", params);

  const [selectDialogOpen, setSelectDialogOpen] = React.useState(false);

  const [replaceAllocation, replaceAllocationResp] =
    useReplacePersonnelAllocationsMutation();

  const [modifyPersonnelAssessment, modifyPersonnelAssessmentParams] =
    useModifyPersonnelAssessmentMutation();

  const bandobastContext = JSON.parse(localStorage.getItem("bandobast")!);
  const timeslotContext = JSON.parse(localStorage.getItem("timeslot")!);
  const locationContext = JSON.parse(localStorage.getItem("location")!);

  const [getDetails, getDetailsParams] = useAllocatedPersonnelDetailsMutation();

  const [allocatedPersonnel, setAllocatedPersonnel] = React.useState<
    Personnel[]
  >([]);

  const [availablePersonnel, setAvailablePersonnel] = React.useState<
    Personnel[]
  >([]);

  const [allocatedCount, setAllocatedCount] = React.useState<number>(0);

  const [showReductionDialog, setShowReductionDialog] =
    React.useState<Boolean>(false);

  React.useEffect(() => {
    getDetails({
      bandobastId: params.bandobastId,
      timeslotId: params.timeslotId,
      body: {
        designationId: params.designationId,
        locationId: params.locationId,
      },
    });
  }, []);

  React.useEffect(() => {
    if (getDetailsParams.isSuccess) {
      console.log("PersonnelAllocation, getDetailsParams:", getDetailsParams);
      setAllocatedPersonnel([...getDetailsParams.data.allocated]);
      setAvailablePersonnel([...getDetailsParams.data.available]);
      setAllocatedCount(getDetailsParams.data.allocated.length);
    }
  }, [getDetailsParams]);

  React.useEffect(() => {
    if (replaceAllocationResp.isSuccess) {
      navigate(-1);
    }
  }, [replaceAllocationResp]);

  const moveToAvailable = (index: number) => {
    // remove from allocated and move to available
    const personnel = allocatedPersonnel.splice(index, 1);
    setAllocatedPersonnel([...allocatedPersonnel]);
    const availablePersonnelUpdated = [...availablePersonnel, ...personnel];
    console.log(
      "moveToAvailable, availablePersonnelUpdated:",
      availablePersonnelUpdated,
    );
    setAvailablePersonnel(availablePersonnelUpdated);
  };

  const columns: ColumnsType<Personnel> = [
    {
      title: strings.photo,
      dataIndex: "photo",
      key: "photo",
      render: (text, record, index) => (
        <img
          src={`${getBaseURL()}/personnel/getPhoto/${record.id}`}
          height={50}
          alt={""}
        />
      ),
    },
    {
      title: strings.name,
      dataIndex: "name",
      key: "name",
    },
    {
      title: strings.designation,
      dataIndex: "designationName",
      key: "designationName",
    },
    {
      title: strings.idNumber,
      dataIndex: "idNumber",
      key: "idNumber",
    },
    {
      title: strings.mobileNumber,
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      render: (text, record, index) => (
        <DeleteButton
          label={strings.remove}
          onClickHandler={() => {
            moveToAvailable(index);
          }}
        />
      ),
    },
  ];

  const countNum = locationContext.count;
  console.log("countNum", countNum);
  console.log("allocatedPersonnel.length=", allocatedPersonnel.length);
  console.log("Equals", allocatedPersonnel.length == countNum);

  return (
    <Box>
      {(getDetailsParams.isLoading || replaceAllocationResp.isLoading) && (
        <ProgressIndicator />
      )}
      <ShowReductionDialog
        state={showReductionDialog}
        agreeHandler={() => {
          replaceAllocation({
            bandobastId: params.bandobastId,
            timeslotId: params.timeslotId,
            body: {
              designationId: locationContext.designationId,
              locationId: params.locationId,
              personnelIds: allocatedPersonnel.map((e: Personnel) => e.id),
            },
          });
          modifyPersonnelAssessment({
            bandobastId: params.bandobastId,
            timeslotId: params.timeslotId,
            locationId: params.locationId,
            personnelAssessmentId: locationContext.assessmentId,
            body: {
              count: allocatedPersonnel.length,
              designationId: params.designationId,
            },
          });
        }}
        disagreeHandler={() => {
          replaceAllocation({
            bandobastId: params.bandobastId,
            timeslotId: params.timeslotId,
            body: {
              designationId: locationContext.designationId,
              locationId: params.locationId,
              personnelIds: allocatedPersonnel.map((e: Personnel) => e.id),
            },
          });
        }}
      />
      <Stack spacing={3}>
        <Title text={strings.personnelAllocation} />
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Desc label={strings.bandobast} value={bandobastContext.name} />
            <Desc label={strings.timeslot} value={timeslotContext.name} />
            <Desc label={strings.location} value={locationContext.name} />
            <Desc
              label={strings.designation}
              value={locationContext.designationName}
            />
            <Desc
              label={strings.assessmentCount}
              value={locationContext.count}
            />
          </Grid>
        </Grid>
        <Stack spacing={0}>
          <Subtitle text={strings.allocatedPersonnel} />
          <Grid item xs={12}>
            <AddButton
              onClickHandler={() => {
                setSelectDialogOpen(true);
              }}
              disabled={allocatedPersonnel.length >= countNum}
            />
          </Grid>
          <SelectPersonnel
            personnel={availablePersonnel}
            onSubmitHandler={(personnel) => {
              setSelectDialogOpen(false);

              const pendingCount = countNum - allocatedPersonnel.length;

              // if extra entries are selected, ignore them.
              personnel = personnel.slice(0, pendingCount);

              // only add upto pendingCount to the set
              const personnelIds = new Set<string>(personnel.map((e) => e.id));
              const availablePersonnelCopy = availablePersonnel.filter(
                (e) => !personnelIds.has(e.id),
              );
              setAvailablePersonnel(availablePersonnelCopy);

              setAllocatedPersonnel((prev: any) => {
                return [...prev, ...personnel];
              });
            }}
            state={selectDialogOpen}
          />
          <Table
            virtual
            scroll={{ y: 400 }}
            columns={columns}
            dataSource={allocatedPersonnel}
            pagination={false}
            rowKey="id"
          />
        </Stack>
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={() => {
              console.log("PersonnelAllocation, old count:", allocatedCount);
              console.log(
                "PersonnelAllocation, new count:",
                allocatedPersonnel.length,
              );
              if (allocatedPersonnel.length < allocatedCount) {
                setShowReductionDialog(true);
              } else {
                replaceAllocation({
                  bandobastId: params.bandobastId,
                  timeslotId: params.timeslotId,
                  body: {
                    designationId: locationContext.designationId,
                    locationId: params.locationId,
                    personnelIds: allocatedPersonnel.map(
                      (e: Personnel) => e.id,
                    ),
                  },
                });
              }
            }}
          >
            {strings.save}
          </Button>
        </Grid>
      </Stack>
    </Box>
  );
}
